import type { CSSProperties } from 'react';

import type { IconName } from '../components/Icon.js';
import type { ApplicationStatus, ClosingVariant } from '../fixtures/domain.js';
import {
  closingVariantFor,
  hiringOffer,
  isHiringStage,
  type HiringStage,
} from '../fixtures/rules.js';

export interface StageMeta {
  icon: IconName;
  /** The custom property naming this stage's colour, for `color-mix` at whatever
   *  strength the surface wants. */
  token: string;
  /** The stage as an offer: buttons, menus, the stepper. */
  name: string;
  /** The stage as a fact: every badge that says where a chat is, past tense. */
  state: string;
  /** The verb form, for prose rather than labels. */
  past: string;
}

/**
 * TWO NAMES PER STAGE, as the design has them: `name` on every control that
 * offers it ("Hire"), `state` on every badge that reports it ("Hired"). One
 * record, so no surface can word either differently from another.
 *
 * `past` has no caller here: the thread note is ajt2.5's own sentence, posted by
 * ajt2.5 for every surface. It is kept so a surface that does need a verb takes
 * this one rather than inventing a fourth vocabulary.
 */
export const STAGE_META: Record<HiringStage, StageMeta> = {
  shortlisted: {
    icon: 'heart',
    token: '--status-shortlisted',
    name: 'Shortlist',
    state: 'Shortlisted',
    past: 'shortlisted',
  },
  interview: {
    icon: 'users',
    token: '--status-interview',
    name: 'Interview',
    state: 'Interviewed',
    past: 'interviewed',
  },
  hired: { icon: 'star', token: '--status-hired', name: 'Hire', state: 'Hired', past: 'hired' },
};

/** The colour a surface draws a status badge in. One name, so
 *  `grep -- --badge-color` lists every surface that draws one. */
export const badgeStyle = (token: string) =>
  ({ '--badge-color': `var(${token})` }) as CSSProperties;

/**
 * How an ending is drawn and named, by what it means rather than by which
 * surface is asking. `closingVariantFor` decides which one a chat gets.
 *
 * The pairs do not mix: "End chat" and the crossed-out bubble belong to a
 * conversation with no application behind it, and putting them on a job chat
 * says it never had one.
 */
export const ENDING_META: Record<ClosingVariant, { icon: IconName; label: string }> = {
  reject: { icon: 'ban', label: 'End & reject' },
  close: { icon: 'chatOff', label: 'End chat' },
};

/** The one step forward, if any, and the ending: all a narrow column offers.
 *  A direct chat has no pipeline, so only its ending. */
export function nextMove(status: ApplicationStatus, jobBounded = true) {
  return {
    next: jobBounded ? hiringOffer(status).stages[0] : undefined,
    ending: ENDING_META[closingVariantFor(jobBounded)],
  };
}

export interface StatusBadge {
  label: string;
  token: string;
  icon: IconName;
  /** Filled for a stage; `ban` draws its slash inside the circle and a fill swallows it. */
  filled: boolean;
}

/** Where a chat stands, as every badge reports it. Rejected is never offered as a
 *  move, so it has no `STAGE_META` entry. Null at pending: nothing decided yet. */
export function statusBadgeOf(status: ApplicationStatus): StatusBadge | null {
  if (status === 'pending') return null;
  if (!isHiringStage(status)) {
    return { label: 'Rejected', token: '--status-rejected', icon: 'ban', filled: false };
  }
  const meta = STAGE_META[status];
  return { label: meta.state, token: meta.token, icon: meta.icon, filled: true };
}
